// /admin/groups · admin-only group management.
// Add/rename/archive top-level groups + subgroups. Backend: GET /groups,
// POST /groups, PATCH /groups/:id, DELETE /groups/:id (soft-delete = archived).

import { useState, useEffect } from 'react';
import * as api from '../lib/api.ts';
import { EditableField } from '../components/EditableField.jsx';
import { isElevated } from '../lib/auth.ts';
import { confirmDialog } from '../lib/confirm.js';

export const AdminGroups = ({ user }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showNewTop, setShowNewTop] = useState(false);
  const [newSubParent, setNewSubParent] = useState(null);

  async function refresh() {
    try {
      const list = await api.listGroups();
      setGroups(list);
      setLoading(false);
    } catch (e) {
      setErr(e.message || '加载失败');
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  if (!isElevated(user)) {
    return <div className="vava-card empty-state"><div>仅管理员 / 店长</div></div>;
  }
  if (loading) return <div className="vava-card empty-state">加载中…</div>;

  const tops = groups.filter(g => !g.parentId).sort((a, b) => a.displayOrder - b.displayOrder);

  async function updateField(id, patch) {
    await api.updateGroup(id, patch);
    await refresh();
  }

  async function archiveGroup(id, name) {
    if (!await confirmDialog({ title: '归档分类', confirmLabel: '归档', message: `确认归档 "${name}"？现有待办的分类会保留，新待办无法选这个分类。` })) return;
    await api.archiveGroup(id);
    await refresh();
  }

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">分类管理</h1>
          <div className="vava-page-meta">管理待办总览的 6 个顶级分类 + 子分类</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewTop(true)}>+ 新建顶级分类</button>
      </div>

      {err && <div className="att-error" role="alert">{err}</div>}

      {tops.map(top => {
        const subs = groups.filter(g => g.parentId === top.id)
          .sort((a, b) => a.displayOrder - b.displayOrder);
        return (
          <section key={top.id} className="groups-admin-section">
            <div className="groups-admin-top">
              <span className="groups-admin-icon">{top.icon || '📁'}</span>
              <div className="groups-admin-name-block">
                <EditableField value={top.name}
                  onSave={(v) => updateField(top.id, { name: v || top.name })}
                  displayClassName="groups-admin-name" />
                <EditableField value={top.description || ''}
                  onSave={(v) => updateField(top.id, { description: v })}
                  placeholder="（添加描述）"
                  displayClassName="groups-admin-desc" />
              </div>
              <div className="groups-admin-actions">
                <span className="groups-admin-order mono">#{top.displayOrder}</span>
                <button className="raci-edit-btn" onClick={() => setNewSubParent(top)}>+ 子分类</button>
                <button className="raci-edit-btn" style={{ color: 'var(--vava-red)' }}
                        onClick={() => archiveGroup(top.id, top.name)}>归档</button>
              </div>
            </div>
            {subs.length > 0 && (
              <ul className="groups-admin-subs">
                {subs.map(s => (
                  <li key={s.id} className="groups-admin-sub">
                    <span className="groups-admin-sub-bullet">└</span>
                    <EditableField value={s.name}
                      onSave={(v) => updateField(s.id, { name: v || s.name })}
                      displayClassName="groups-admin-sub-name" />
                    <EditableField value={s.description || ''}
                      onSave={(v) => updateField(s.id, { description: v })}
                      placeholder="（描述）"
                      displayClassName="groups-admin-sub-desc" />
                    <span className="groups-admin-sub-tail">
                      <span className="groups-admin-order mono">#{s.displayOrder}</span>
                      <button className="raci-edit-btn"
                              onClick={() => archiveGroup(s.id, s.name)}
                              style={{ color: 'var(--vava-red)' }}>归档</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {showNewTop && (
        <NewGroupModal onClose={() => setShowNewTop(false)} onSaved={refresh}
                       parent={null} existing={groups} />
      )}
      {newSubParent && (
        <NewGroupModal onClose={() => setNewSubParent(null)} onSaved={refresh}
                       parent={newSubParent} existing={groups} />
      )}
    </>
  );
};

const NewGroupModal = ({ parent, existing, onClose, onSaved }) => {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [displayOrder, setDisplayOrder] = useState(100);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    if (!id.trim() || !name.trim()) {
      setErr('id 和 name 必填');
      return;
    }
    if (existing.some(g => g.id === id.trim())) {
      setErr('id 已存在');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      await api.createGroup({
        id: id.trim(),
        name: name.trim(),
        parent_id: parent?.id ?? null,
        display_order: Number(displayOrder) || 100,
        description: description.trim() || undefined,
        icon: icon.trim() || undefined
      });
      await onSaved();
      onClose();
    } catch (e) {
      setErr(e.message || '创建失败');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vava-modal-backdrop" onClick={onClose}>
      <div className="vava-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vava-modal-head">
          <h2>{parent ? `在「${parent.name}」下新建子分类` : '新建顶级分类'}</h2>
          <button className="vava-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="vava-modal-body">
          <div className="vava-field">
            <label className="vava-label">ID（不可改，建议简短英文 / 大写）*</label>
            <input className="vava-input" value={id} onChange={(e) => setId(e.target.value)}
                   placeholder={parent ? `${parent.id}_XX` : 'G_XX'} />
          </div>
          <div className="vava-field">
            <label className="vava-label">名称 *</label>
            <input className="vava-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="如：合作" />
          </div>
          {!parent && (
            <div className="vava-field">
              <label className="vava-label">图标（emoji）</label>
              <input className="vava-input" value={icon} onChange={(e) => setIcon(e.target.value)}
                     placeholder="🤝" maxLength={4} />
            </div>
          )}
          <div className="vava-field">
            <label className="vava-label">描述</label>
            <textarea className="vava-textarea" rows={2}
                      value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="vava-field">
            <label className="vava-label">排序（数字越小越靠前）</label>
            <input className="vava-input" type="number" value={displayOrder}
                   onChange={(e) => setDisplayOrder(e.target.value)} />
          </div>
          {err && <div className="att-error" role="alert">{err}</div>}
        </div>
        <div className="vava-modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '创建中…' : '创建'}
          </button>
        </div>
      </div>
    </div>
  );
};
